import { AlertTriangle, RefreshCw } from "lucide-react";

import { Button } from "@/ui/Button";

/**
 * What the available-targets listing says about this Project's target.
 *
 * `/v1/preview-targets` is derived from provisioned credentials rather than
 * from a catalogue, so it changes without a deployment and an EMPTY list is a
 * legitimate answer rather than an error — the contract says so in as many
 * words. A Project keeps naming its target through all of that, which is why
 * the target being displayed and the target being usable are two different
 * questions and this component answers only the second.
 */
export type PreviewTargetAvailability =
  /** The listing came back empty: no credential is provisioned anywhere yet. */
  | "none_provisioned"
  /** The listing came back without this Project's target in it. */
  | "not_available"
  /** The listing could not be read, so nothing is claimed in either direction. */
  | "unchecked";

/**
 * Identifier-free, and deliberately so.
 *
 * Both unavailable states are recoverable — a credential gets provisioned, or
 * the target is changed on the Project — so the copy states the situation and
 * the way out rather than reading as a fault. Since #338 that second way out is
 * literal: the `preview.configure`-gated picker sits directly beneath this
 * notice, for a caller that holds the capability. This sentence named #203 as
 * the owner of that control until then, which stopped being true on 2026-08-11
 * when the amendment moved every *Preview as a patient* requirement into #8's
 * slices and #203 closed.
 *
 * None of it names the target, the project, or a request id: the target id is
 * already on screen above as a fact about the Project, and repeating it inside
 * the explanation turns a recoverable state into an error message about a value
 * the clinician cannot act on. That constraint is #260's own acceptance
 * criterion, and it is the same posture `NewProjectDialog` takes for the
 * identical 422.
 */
export const PREVIEW_TARGET_COPY = {
  none_provisioned:
    "No preview target is provisioned for this deployment yet, so previews cannot authenticate against anything. Ask a workspace administrator to provision one, then refresh.",
  not_available:
    "This project's preview target is not currently available, so previews against it cannot authenticate. It becomes usable again when its credential is provisioned, or when the project is pointed at an available target.",
  unchecked:
    "The available preview targets could not be read, so whether this project's target is currently usable is unknown. Nothing has been assumed in either direction.",
} satisfies Record<PreviewTargetAvailability, string>;

/**
 * The 422 the freeze can answer with is the same fact arriving by a different
 * route: the service refusing a target the listing would also have omitted. It
 * reads the same sentence rather than a second wording of one situation.
 */
export const PREVIEW_TARGET_NOT_ENABLED_COPY =
  PREVIEW_TARGET_COPY.not_available;

export function PreviewTargetNotice({
  availability,
  onRefresh,
}: {
  availability: PreviewTargetAvailability;
  onRefresh: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-foreground"
    >
      <span aria-hidden="true" className="text-muted-foreground">
        <AlertTriangle className="h-4 w-4" />
      </span>
      <p className="min-w-0 flex-1">{PREVIEW_TARGET_COPY[availability]}</p>
      <Button variant="outline" size="sm" onClick={onRefresh}>
        <RefreshCw aria-hidden="true" className="h-4 w-4" />
        Check again
      </Button>
    </div>
  );
}
